import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Breadcrumbs,
  Link,
  Button,
  CircularProgress,
  Tooltip,
  Divider,
  Chip,
  LinearProgress
} from '@material-ui/core'
import {
  Close as CloseIcon,
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  Home as HomeIcon,
  Storage as StorageIcon,
  Description as LogIcon,
  Image as ImageIcon,
  Code as CodeIcon,
  Archive as ArchiveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@material-ui/icons'
import { Alert } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import * as vncFilesAPI from '../data/api/vnc-files'

const useStyles = makeStyles(theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 700,
      maxWidth: 900,
      height: '80vh',
      maxHeight: 700,
      borderRadius: 12,
      overflow: 'hidden'
    }
  },
  titleBar: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    color: '#fff',
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  titleIcon: {
    background: 'rgba(255,255,255,0.1)',
    borderRadius: 8,
    padding: 8,
    display: 'flex'
  },
  serverChip: {
    backgroundColor: 'rgba(76, 175, 80, 0.2)',
    color: '#81c784',
    borderColor: '#81c784',
    fontSize: '0.75rem'
  },
  closeButton: {
    color: 'rgba(255,255,255,0.7)',
    '&:hover': {
      color: '#fff',
      backgroundColor: 'rgba(255,255,255,0.1)'
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  breadcrumbs: {
    flex: 1,
    '& .MuiBreadcrumbs-separator': {
      color: '#9e9e9e'
    }
  },
  breadcrumbLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    color: '#546e7a',
    fontSize: '0.875rem',
    '&:hover': {
      color: '#1976d2',
      textDecoration: 'underline'
    }
  },
  breadcrumbCurrent: {
    color: '#37474f',
    fontWeight: 500,
    fontSize: '0.875rem'
  },
  content: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  listContainer: {
    flex: 1,
    overflow: 'auto',
    backgroundColor: '#fff'
  },
  listItem: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: '#f5f9ff'
    }
  },
  listItemDirectory: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#e3f2fd'
    }
  },
  folderIcon: {
    color: '#ffa726'
  },
  fileIcon: {
    color: '#78909c'
  },
  logIcon: {
    color: '#66bb6a'
  },
  imageIcon: {
    color: '#7e57c2'
  },
  codeIcon: {
    color: '#29b6f6'
  },
  archiveIcon: {
    color: '#ef5350'
  },
  fileSize: {
    color: '#9e9e9e',
    fontSize: '0.75rem',
    marginRight: theme.spacing(2)
  },
  fileDate: {
    color: '#bdbdbd',
    fontSize: '0.7rem'
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8),
    color: '#9e9e9e'
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.8)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    backgroundColor: '#fafafa',
    borderTop: '1px solid #e0e0e0',
    fontSize: '0.75rem',
    color: '#757575'
  },
  actionButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5, 1.5),
    fontSize: '0.75rem'
  },
  uploadInput: {
    display: 'none'
  },
  downloadProgress: {
    width: 100,
    marginRight: theme.spacing(1)
  }
}))

// Helper to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// Helper to get file icon based on extension
const getFileIcon = (filename, classes) => {
  const ext = filename.split('.').pop().toLowerCase()
  
  const logExtensions = ['log', 'txt', 'csv']
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'ico']
  const codeExtensions = ['xml', 'json', 'ini', 'cfg', 'conf', 'bat', 'ps1', 'sh']
  const archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz']
  
  if (logExtensions.includes(ext)) {
    return <LogIcon className={classes.logIcon} />
  }
  if (imageExtensions.includes(ext)) {
    return <ImageIcon className={classes.imageIcon} />
  }
  if (codeExtensions.includes(ext)) {
    return <CodeIcon className={classes.codeIcon} />
  }
  if (archiveExtensions.includes(ext)) {
    return <ArchiveIcon className={classes.archiveIcon} />
  }
  return <FileIcon className={classes.fileIcon} />
}

// Parse path into segments
const parsePath = (path) => {
  if (!path) return []
  // Normalize path separators
  const normalized = path.replace(/\\/g, '/')
  const segments = normalized.split('/').filter(s => s)
  return segments
}

// Build path from segments
const buildPath = (segments) => {
  if (segments.length === 0) return ''
  // First segment might be drive letter (e.g., "C:")
  return segments.join('/')
}

const FileBrowserModal = ({ open, onClose, modalityId, modalityName, initialPath = 'C:' }) => {
  const classes = useStyles()
  const fileInputRef = useRef(null)
  
  const [currentPath, setCurrentPath] = useState(initialPath)
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [connectionStatus, setConnectionStatus] = useState(null) // null, 'testing', 'connected', 'error'
  const [downloadingFile, setDownloadingFile] = useState(null)
  const [uploadingFile, setUploadingFile] = useState(null)
  const [statusMessage, setStatusMessage] = useState('')

  // Test connection when modal opens
  useEffect(() => {
    if (open && modalityId) {
      testConnection()
    }
  }, [open, modalityId])

  // Load directory when path changes
  useEffect(() => {
    if (open && modalityId && connectionStatus === 'connected' && currentPath) {
      loadDirectory(currentPath)
    }
  }, [open, modalityId, connectionStatus, currentPath])

  const testConnection = async () => {
    setConnectionStatus('testing')
    setError(null)
    try {
      await vncFilesAPI.testConnection(modalityId)
      setConnectionStatus('connected')
      setStatusMessage('Connected to VNC file transfer')
    } catch (err) {
      setConnectionStatus('error')
      setError(err.message || 'Failed to connect to VNC server')
    }
  }

  const loadDirectory = async (path) => {
    setLoading(true)
    setError(null)
    console.log('[FileBrowser] Loading directory:', path)
    try {
      const result = await vncFilesAPI.listDirectory(modalityId, path)
      console.log('[FileBrowser] Result:', { path: result.path, fileCount: result.files?.length || result.entries?.length })
      // Sort: directories first, then files, both alphabetically
      const sorted = [...(result.files || result.entries || [])].sort((a, b) => {
        if (a.isDirectory && !b.isDirectory) return -1
        if (!a.isDirectory && b.isDirectory) return 1
        return a.name.localeCompare(b.name)
      })
      setEntries(sorted)
      setStatusMessage(`${sorted.length} items`)
    } catch (err) {
      setError(err.message || 'Failed to load directory')
      setEntries([])
    } finally {
      setLoading(false)
    }
  }

  const handleNavigate = useCallback((path) => {
    setCurrentPath(path)
  }, [])

  const handleEntryClick = useCallback((entry) => {
    if (entry.isDirectory) {
      const newPath = currentPath ? `${currentPath}/${entry.name}` : entry.name
      setCurrentPath(newPath)
    }
  }, [currentPath])

  const handleGoBack = useCallback(() => {
    const segments = parsePath(currentPath)
    if (segments.length > 1) {
      segments.pop()
      setCurrentPath(buildPath(segments))
    }
  }, [currentPath])

  const handleDownload = useCallback(async (entry) => {
    if (entry.isDirectory) return
    
    const filePath = `${currentPath}/${entry.name}`
    setDownloadingFile(entry.name)
    setStatusMessage(`Downloading ${entry.name}...`)
    
    try {
      const result = await vncFilesAPI.downloadFile(modalityId, filePath)
      vncFilesAPI.downloadToBrowser(result.content, entry.name)
      setStatusMessage(`Downloaded ${entry.name} (${formatFileSize(result.size)})`)
    } catch (err) {
      setError(`Failed to download ${entry.name}: ${err.message}`)
    } finally {
      setDownloadingFile(null)
    }
  }, [modalityId, currentPath])

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    setUploadingFile(file.name)
    setStatusMessage(`Uploading ${file.name}...`)
    
    try {
      const base64Content = await vncFilesAPI.fileToBase64(file)
      const remotePath = `${currentPath}/${file.name}`
      await vncFilesAPI.uploadFile(modalityId, remotePath, base64Content)
      setStatusMessage(`Uploaded ${file.name} successfully`)
      // Refresh directory
      loadDirectory(currentPath)
    } catch (err) {
      setError(`Failed to upload ${file.name}: ${err.message}`)
    } finally {
      setUploadingFile(null)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRefresh = () => {
    loadDirectory(currentPath)
  }

  const handleClose = () => {
    setEntries([])
    setError(null)
    setConnectionStatus(null)
    setCurrentPath(initialPath)
    onClose()
  }

  const pathSegments = parsePath(currentPath)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialog}
      maxWidth={false}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Title Bar */}
      <Box className={classes.titleBar}>
        <Box className={classes.titleContent}>
          <Box className={classes.titleIcon}>
            <StorageIcon />
          </Box>
          <Box>
            <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
              File Browser
            </Typography>
            <Typography variant="caption" style={{ opacity: 0.7 }}>
              {modalityName || `Modality ${modalityId}`}
            </Typography>
          </Box>
          {connectionStatus === 'connected' && (
            <Chip
              size="small"
              icon={<CheckCircleIcon style={{ color: '#81c784', fontSize: 14 }} />}
              label="Connected"
              variant="outlined"
              className={classes.serverChip}
            />
          )}
        </Box>
        <IconButton className={classes.closeButton} onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent className={classes.content}>
        {/* Connection Error */}
        {connectionStatus === 'error' && (
          <Box p={4} textAlign="center">
            <ErrorIcon style={{ fontSize: 48, color: '#ef5350', marginBottom: 16 }} />
            <Typography variant="h6" gutterBottom>Connection Failed</Typography>
            <Typography color="textSecondary" paragraph>{error}</Typography>
            <Button variant="contained" color="primary" onClick={testConnection}>
              Retry Connection
            </Button>
          </Box>
        )}

        {/* Connecting */}
        {connectionStatus === 'testing' && (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={48} />
            <Typography style={{ marginTop: 16 }}>Connecting to VNC server...</Typography>
          </Box>
        )}

        {/* Connected - Show File Browser */}
        {connectionStatus === 'connected' && (
          <>
            {/* Toolbar */}
            <Box className={classes.toolbar}>
              <Tooltip title="Go back">
                <span>
                  <IconButton 
                    size="small" 
                    onClick={handleGoBack}
                    disabled={pathSegments.length <= 1 || loading}
                  >
                    <BackIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Divider orientation="vertical" flexItem style={{ margin: '0 8px' }} />
              
              {/* Breadcrumbs */}
              <Breadcrumbs className={classes.breadcrumbs} maxItems={4}>
                {pathSegments.map((segment, index) => {
                  const isLast = index === pathSegments.length - 1
                  const pathToHere = buildPath(pathSegments.slice(0, index + 1))
                  
                  if (isLast) {
                    return (
                      <Typography key={index} className={classes.breadcrumbCurrent}>
                        {segment}
                      </Typography>
                    )
                  }
                  
                  return (
                    <Link
                      key={index}
                      className={classes.breadcrumbLink}
                      onClick={() => handleNavigate(pathToHere)}
                    >
                      {index === 0 && <HomeIcon style={{ fontSize: 16 }} />}
                      {segment}
                    </Link>
                  )
                })}
              </Breadcrumbs>
              
              <input
                ref={fileInputRef}
                type="file"
                className={classes.uploadInput}
                onChange={handleFileSelected}
              />
              
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={uploadingFile ? <CircularProgress size={14} /> : <UploadIcon />}
                onClick={handleUploadClick}
                disabled={loading || !!uploadingFile}
                className={classes.actionButton}
              >
                {uploadingFile ? 'Uploading...' : 'Upload'}
              </Button>
            </Box>

            {/* Loading Bar */}
            {loading && <LinearProgress />}

            {/* Error Alert */}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* File List */}
            <Box className={classes.listContainer} style={{ position: 'relative' }}>
              {entries.length === 0 && !loading ? (
                <Box className={classes.emptyState}>
                  <FolderIcon style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }} />
                  <Typography variant="h6">Empty Directory</Typography>
                  <Typography variant="body2">This folder contains no files or folders</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {entries.map((entry, index) => (
                    <ListItem
                      key={index}
                      className={`${classes.listItem} ${entry.isDirectory ? classes.listItemDirectory : ''}`}
                      onClick={() => handleEntryClick(entry)}
                      button={entry.isDirectory}
                    >
                      <ListItemIcon style={{ minWidth: 40 }}>
                        {entry.isDirectory ? (
                          <FolderIcon className={classes.folderIcon} />
                        ) : (
                          getFileIcon(entry.name, classes)
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={entry.name}
                        secondary={entry.modTime || entry.modifiedTime ? formatDate(entry.modTime || entry.modifiedTime) : null}
                        primaryTypographyProps={{ style: { fontWeight: entry.isDirectory ? 500 : 400 } }}
                      />
                      {!entry.isDirectory && (
                        <ListItemSecondaryAction>
                          <Typography component="span" className={classes.fileSize}>
                            {formatFileSize(entry.size)}
                          </Typography>
                          {downloadingFile === entry.name ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Tooltip title="Download">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDownload(entry)
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            {/* Status Bar */}
            <Box className={classes.statusBar}>
              <Typography variant="caption">{statusMessage}</Typography>
              <Typography variant="caption">
                Path: {currentPath}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default FileBrowserModal

